import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppNavigationLeaveProvider, AppNavigationLink, guardAppTabPress, useAppNavigationLeave } from '@/components/navigation/AppNavigationLeave';
import { Stack, Tabs, Slot, Link, usePathname, router } from 'expo-router';
import { observer } from '@legendapp/state/react';
import { View, StyleSheet, Platform, Pressable, Text, Animated, TouchableOpacity, useWindowDimensions, Linking, AppState, type AppStateStatus, type ColorValue } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { getLogger, TabNavigatorLogger } from '../../utils/logger';
import theme from '../../config/theme';
import globalState, { appActions, userActions } from '../../state/globalState';
import { initializeSyncService, checkSyncPrerequisites, syncAll, getLastSyncTimestamp, isInitialBootstrapComplete, markInitialBootstrapComplete, hasEligiblePendingSyncWork, registerClientInstallationSyncHook } from '../../services/syncService';
import { startPeriodicSync, stopPeriodicSync, triggerSyncSoon } from '../../services/syncScheduler';
import { setupRealtimeSubscriptions, cleanupRealtimeSubscriptions } from '../../services/realtimeService';
import { checkSubscriptionStatus } from '../../services/subscriptionService';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { LinearGradient } from 'expo-linear-gradient';
import { hasDeveloperBypass, hasPremiumAccess } from '@/utils/subscriptionAccess';
import { ReactNode } from 'react';
import { canAccessChecklistDashboard } from '@/utils/userRoles';
import { recoverPendingCameraResult } from '@/utils/media/pendingImagePickerRecovery';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';
import { IS_CHECKLIST_SYNC_RECOVERY_CANARY } from '@/config/recoveryCanary';
import {
  decideRecoverySubscriptionLookup,
  decideRecoveryReconnectAttempt,
  canRunRecoveryReconnect,
  evaluateRecoveryRuntimeGate,
  getRecoverySubscriptionLookupContext,
  isRecoveryRuntimeBlocked,
  recoveryRuntimeGate,
  shouldBlockRecoveryUi,
  type RecoveryRuntimeGateSnapshot,
} from '@/services/recovery/RecoveryRuntimeGate';
import {
  appRecoveryRuntime,
  getRecoveryServiceReleaseId,
  type AppRecoveryRuntimeSnapshot,
} from '@/services/recovery/appRecoveryRuntime';
import { RecoveryGateScreen } from '@/components/recovery/RecoveryGateScreen';
import { bindSupportDiagnosticIdentity, checkPendingSupportDiagnosticRequests } from '@/services/diagnostics/supportDiagnosticRuntime';
import { checkPendingSupportFullStateRequests } from '@/services/diagnostics/supportFullStateRuntime';
import { reportClientInstallationPresenceBestEffort } from '@/services/runtime/clientInstallationPresence';
import { beginPostUpdateBootstrap, checkPendingSupportSyncRequests, isPostUpdateBootstrapBusy, runPostUpdateCollection } from '@/services/runtime/postUpdateCollectionCoordinator';
import { enqueueLegacySupportCheck } from '@/services/runtime/supportCollectionQueue';
import { AppUpdatePromptModal } from '@/components/AppUpdatePromptModal';
import { flushBufferedLogEvents } from '@/utils/logBuffer';
import {
  checkAppUpdatePromptBestEffort,
  subscribeAppUpdatePrompt,
  type AppUpdatePrompt,
} from '@/services/runtime/appUpdatePrompt';
//import { debugPause } from '@/utils/debugPause';

const DESKTOP_BREAKPOINT = APP_CONSTANTS.UI?.DESKTOP_BREAKPOINT ?? 1024;

export const isBootstrapIdentityCurrent = (input: {
  execution: number;
  activeExecution: number;
  expectedUserId: string | undefined;
  currentUserId: string | undefined;
  expectedCompanyId?: string;
  currentCompanyId?: string | null;
}): boolean =>
  input.execution === input.activeExecution &&
  Boolean(input.expectedUserId) &&
  input.expectedUserId === input.currentUserId &&
  (!input.expectedCompanyId || input.expectedCompanyId === input.currentCompanyId);

export const isSupportRequestIdentityReady = (input: {
  isAuthenticated: boolean;
  isDatabaseInitialized: boolean;
  isNetworkConnected: boolean;
  recoveryBlocked: boolean;
  userId?: string;
  profileId?: string | null;
  profileCompanyId?: string | null;
  companyId?: string | null;
}): boolean => Boolean(
  input.isAuthenticated &&
  input.isDatabaseInitialized &&
  input.isNetworkConnected &&
  !input.recoveryBlocked &&
  input.userId &&
  input.profileId === input.userId &&
  input.profileCompanyId &&
  input.profileCompanyId === input.companyId
);

// Logger específico para este componente
const logger = getLogger('APP-(APP)-LAYOUT');

// Interface para o componente NavLink
interface NavLinkProps {
  href: string;
  icon: ReactNode;
  label: string;
  isActive?: boolean;
}

// Componente NavLink para a sidebar na versão web
const NavLink = ({ href, icon, label, isActive = false }: NavLinkProps) => {
  // Achatar o array de estilos em um único objeto para evitar problemas no DOM web
  const linkStyle = StyleSheet.flatten([
    styles.navLink,
    isActive && styles.navLinkActive
  ]);

  // Fazer o mesmo para o texto
  const textStyle = StyleSheet.flatten([
    styles.navLinkText,
    isActive && styles.navLinkTextActive
  ]);

  return (
    <AppNavigationLink href={href}>
      <TouchableOpacity
        style={linkStyle}
        accessibilityLabel={label}
        accessibilityRole="link"
        accessibilityState={{ selected: isActive }}
      >
        <View style={styles.navLinkIcon}>
          {icon}
        </View>
        <Text style={textStyle}>
          {label}
        </Text>
      </TouchableOpacity>
    </AppNavigationLink>
  );
};

const AppLayoutContent = observer(() => {
  const appNavigationLeave = useAppNavigationLeave();
  //logger.info('Renderizando AppLayout');
  useEffect(() => {
    TabNavigatorLogger.Main.debug('Inicializando tab navigator');
  }, []);

  // Estado local para controlar a verificação de assinatura
  const [isChecking, setIsChecking] = useState(false);
  // Estado para a animação do ícone ativo
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [recoveryRuntimeSnapshot, setRecoveryRuntimeSnapshot] =
    useState<RecoveryRuntimeGateSnapshot>(() => recoveryRuntimeGate.getSnapshot());
  const [appRecoverySnapshot, setAppRecoverySnapshot] =
    useState<AppRecoveryRuntimeSnapshot>(() => appRecoveryRuntime.getSnapshot());
  const [appUpdatePrompt, setAppUpdatePrompt] = useState<AppUpdatePrompt | null>(null);
  const [recoveryReleaseEpoch, setRecoveryReleaseEpoch] = useState(0);
  const releasedRecoveryIdRef = useRef<string | null>(null);
  const [isRecoveryAppActive, setIsRecoveryAppActive] =
    useState(() => Platform.OS === 'web' || AppState.currentState === 'active');
  const iconScale = useRef(new Animated.Value(1)).current;

  // Adiciona hook para pegar a URL atual na web
  const pathname = usePathname();
  useEffect(() => {
    logger.info(`Current pathname: ${pathname}`);
  }, [pathname]);

  // Referência para controlar inicialização em andamento das subscrições realtime
  const realtimeSetupInProgress = useRef(false);
  // Referência para controlar sincronização em andamento
  const syncInProgress = useRef(false);
  const bootstrapExecutionRef = useRef(0);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);
  const webWasHiddenRef = useRef(false);
  const isCheckingRef = useRef(false);
  const subscriptionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingCameraRecoveryInProgressRef = useRef(false);
  const recoveryReconnectAttemptRef = useRef<string | null>(null);
  const recoveryReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectExecutionRef = useRef(0);

  // Estados do usuário e pré-requisitos
  const isAuthenticated = globalState.user.isAuthenticated.get();
  const userId = globalState.user.user.get()?.id;
  const profileCompanyId = globalState.user.profile.get()?.empresa_id;
  const userExistsInDatabase = globalState.user.existsInDatabase.get();
  const isNetworkConnected = globalState.network.isConnected.get();
  const isDatabaseInitialized = globalState.database.isInitialized.get();

  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;
  const safeAreaInsets = useSafeAreaInsets();
  const androidBottomInset = Platform.OS === 'android' ? safeAreaInsets.bottom : 0;
  const webClientDescriptor = useMemo(() => {
    if (Platform.OS !== 'web' || typeof globalThis === 'undefined' || !globalThis.navigator) {
      return 'native';
    }

    const ua = globalThis.navigator.userAgent?.toLowerCase?.() ?? '';
    if (ua.includes('android')) return 'web-android';
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'web-ios';
    if (ua.includes('windows')) return 'web-windows';
    if (ua.includes('mac os')) return 'web-macos';
    return 'web-desktop';
  }, []);
  const isDesktopLayout = Platform.OS === 'web' && width >= DESKTOP_BREAKPOINT;
  const isMobileExperience = !isDesktopLayout;
  const subscriptionSnapshot = globalState.subscription.get();
  const profileSnapshot = globalState.user.profile.get();
  const companySnapshot = globalState.company.data.get();
  const supportBufferIdentityReady = Boolean(
    userId &&
    profileSnapshot?.id === userId &&
    profileSnapshot?.empresa_id
  );
  const supportRequestIdentityReady = isSupportRequestIdentityReady({
    isAuthenticated,
    isDatabaseInitialized,
    isNetworkConnected,
    recoveryBlocked: recoveryRuntimeSnapshot.blocked,
    userId,
    profileId: profileSnapshot?.id,
    profileCompanyId: profileSnapshot?.empresa_id,
    companyId: companySnapshot?.id,
  });
  const checkPendingSupportRequests = useCallback((trigger: string) => {
    if (!supportRequestIdentityReady) {
      logger.debug(`[SUPPORT-REQUEST] Check ignorado (${trigger}): identidade ainda incompleta`);
      return;
    }
    if (isPostUpdateBootstrapBusy(userId)) {
      logger.debug(`[SUPPORT-REQUEST] Check adiado (${trigger}): bootstrap ainda em execução`);
      return;
    }
    const bootstrapScope = { companyId: profileSnapshot?.empresa_id, userId };
    const allowAfterBootstrapAttempt = trigger === 'bootstrap_finished';
    if (!allowAfterBootstrapAttempt && !isInitialBootstrapComplete(bootstrapScope)) {
      logger.debug(`[SUPPORT-REQUEST] Check adiado (${trigger}): bootstrap ainda não confirmado`);
      return;
    }
    // O coordenador novo e os verificadores legados compartilham a fila. A
    // exportação longa não segura SyncExecutionLease e a tela não aguarda.
    void runPostUpdateCollection(trigger, { afterSync: allowAfterBootstrapAttempt })
      .catch(() => undefined)
      .then(() => enqueueLegacySupportCheck(async () => {
        await checkPendingSupportSyncRequests(trigger);
        await checkPendingSupportDiagnosticRequests(trigger);
        await checkPendingSupportFullStateRequests(trigger);
      }))
      .catch(() => undefined);
  }, [profileSnapshot?.empresa_id, supportRequestIdentityReady, userId]);
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, profileSnapshot);
  const isDeveloper = hasDeveloperBypass(profileSnapshot?.user_type);
  const showAdminArea = premiumAccess && canAccessChecklistDashboard(profileSnapshot?.user_type);

  useEffect(() => recoveryRuntimeGate.subscribe(setRecoveryRuntimeSnapshot), []);
  useEffect(() => appRecoveryRuntime.subscribe(setAppRecoverySnapshot), []);
  useEffect(() => subscribeAppUpdatePrompt(next => setAppUpdatePrompt(next.prompt)), []);
  useEffect(() => registerClientInstallationSyncHook(reportClientInstallationPresenceBestEffort), []);
  useEffect(() => {
    if (supportBufferIdentityReady) {
      bindSupportDiagnosticIdentity(profileSnapshot?.empresa_id, userId);
    } else if (!isAuthenticated && !globalState.user.isLoading.get()) {
      bindSupportDiagnosticIdentity(null, null);
    }
    if (supportRequestIdentityReady) {
      checkPendingSupportRequests('bootstrap_or_reconnect');
      checkAppUpdatePromptBestEffort(true);
    }
  }, [checkPendingSupportRequests, isAuthenticated, profileSnapshot?.empresa_id, supportBufferIdentityReady, supportRequestIdentityReady, userId]);

  useEffect(() => {
    appRecoveryRuntime.configureAutoVerification({
      identity: recoveryRuntimeSnapshot.status === 'recovery_blocked'
        ? recoveryRuntimeSnapshot.identity
        : null,
      isOnline: isNetworkConnected,
      isActive: isRecoveryAppActive,
    });
  }, [
    isNetworkConnected,
    isRecoveryAppActive,
    recoveryRuntimeSnapshot.blocked,
    recoveryRuntimeSnapshot.status,
    recoveryRuntimeSnapshot.identity?.companyId,
    recoveryRuntimeSnapshot.identity?.deviceId,
    recoveryRuntimeSnapshot.identity?.userId,
  ]);

  useEffect(() => () => appRecoveryRuntime.stopAutoVerification(), []);

  useEffect(() => {
    const releaseId = getRecoveryServiceReleaseId(
      releasedRecoveryIdRef.current,
      appRecoverySnapshot
    );
    if (!releaseId) return;
    releasedRecoveryIdRef.current = releaseId;
    setRecoveryReleaseEpoch(value => value + 1);
  }, [appRecoverySnapshot.recoveryId, appRecoverySnapshot.status]);

  // Cada identidade autenticada precisa começar fail-closed. Sem este reset,
  // um usuário poderia herdar por alguns instantes o estado "allowed" anterior.
  useEffect(() => {
    if (!IS_CHECKLIST_SYNC_RECOVERY_CANARY) return;
    recoveryRuntimeGate.reset();
    appRecoveryRuntime.resetView();
    cleanupRealtimeSubscriptions();
    stopPeriodicSync();
  }, [isAuthenticated, userId]);

  useEffect(() => {
    const identity = recoveryRuntimeSnapshot.identity;
    if (!IS_CHECKLIST_SYNC_RECOVERY_CANARY || !recoveryRuntimeSnapshot.blocked || !identity) {
      return;
    }
    void appRecoveryRuntime.hydrate(identity);
  }, [
    recoveryRuntimeSnapshot.blocked,
    recoveryRuntimeSnapshot.identity?.companyId,
    recoveryRuntimeSnapshot.identity?.deviceId,
    recoveryRuntimeSnapshot.identity?.userId,
  ]);

  const handleRecoveryConsent = useCallback(async () => {
    const identity = recoveryRuntimeSnapshot.identity;
    if (!identity) return;
    await appRecoveryRuntime.consentAndStart(identity);
  }, [recoveryRuntimeSnapshot.identity]);

  const handleRecoveryResume = useCallback(async () => {
    const identity = recoveryRuntimeSnapshot.identity;
    if (!identity) return;
    await appRecoveryRuntime.resume(identity);
  }, [recoveryRuntimeSnapshot.identity]);

  const handleRecoveryVerify = useCallback(async () => {
    const identity = recoveryRuntimeSnapshot.identity;
    if (!identity) return;
    await appRecoveryRuntime.verifyPreservation(identity);
  }, [recoveryRuntimeSnapshot.identity]);

  const runPendingCameraRecovery = useCallback(async (trigger: string) => {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      return;
    }

    if (shouldBlockRecoveryUi(recoveryRuntimeSnapshot)) {
      logger.warn(`[RECOVERY] Recuperacao de camera (${trigger}) pausada pelo gate runtime`);
      return;
    }

    if (Platform.OS !== 'android' || pendingCameraRecoveryInProgressRef.current) {
      return;
    }

    pendingCameraRecoveryInProgressRef.current = true;
    try {
      const outcome = await recoverPendingCameraResult(trigger, {
        pathname,
        isAuthenticated,
        userId,
        userExistsInDatabase,
        isDatabaseInitialized,
      });

      if (outcome.status !== 'stored') {
        return;
      }

      logger.info(
        `[PENDING-CAMERA] resultado recuperado para checklist=${outcome.context.checklistId || 'n/a'} campo=${outcome.context.entityId || 'n/a'}`
      );

      if (pathname !== outcome.routePath) {
        router.replace(outcome.routePath as never);
      }
    } finally {
      pendingCameraRecoveryInProgressRef.current = false;
    }
  }, [
    isAuthenticated,
    isDatabaseInitialized,
    pathname,
    recoveryRuntimeSnapshot.status,
    userExistsInDatabase,
    userId,
  ]);

  const handleOpenWhatsApp = useCallback(() => {
    const whatsappUrl = APP_CONSTANTS.CONTACTS?.WHATSAPP_URL;
    if (!whatsappUrl) {
      logger.warn('[LAYOUT] URL do WhatsApp não configurada em APP_CONSTANTS.CONTACTS.WHATSAPP_URL');
      return;
    }
    Linking.openURL(whatsappUrl).catch(error => {
      logger.error('[LAYOUT] Falha ao abrir WhatsApp:', error);
    });
  }, []);

  useEffect(() => {
    logger.info(
      `[LAYOUT] Dimensões atualizadas - width: ${width}, height: ${height}, isLandscape: ${isLandscape}, baseOS: ${Platform.OS}, agente: ${webClientDescriptor}, layout: ${isDesktopLayout ? 'web/sidebar' : 'tabs/mobile'}, breakpoint: ${DESKTOP_BREAKPOINT}, modo: ${isMobileExperience ? 'mobile' : 'desktop'}`
    );
  }, [height, isDesktopLayout, isLandscape, isMobileExperience, webClientDescriptor, width]);

  useEffect(() => {
    logger.info(
      `[LAYOUT] Branch ativo: ${isDesktopLayout ? 'web/sidebar' : 'tabs/mobile'} - pathname atual: ${pathname} - plataforma: ${Platform.OS}/${webClientDescriptor}`
    );
  }, [isDesktopLayout, pathname, webClientDescriptor]);

  useEffect(() => {
    void runPendingCameraRecovery('layout_mounted');
  }, [runPendingCameraRecovery]);

  // Ao voltar do background/aba oculta, antecipamos o mesmo sync do scheduler
  // para recuperar alterações remotas que possam ter sido perdidas pelo realtime.
  const triggerResumeCatchUpSync = useCallback(
    (source: 'app-resume-mobile' | 'app-resume-web') => {
      if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
        logger.warn(`[RECOVERY] Sync de retorno (${source}) bloqueado pela quarentena`);
        return;
      }

      if (IS_CHECKLIST_SYNC_RECOVERY_CANARY && isRecoveryRuntimeBlocked()) {
        logger.warn(`[RECOVERY] Sync de retorno (${source}) bloqueado pelo gate runtime`);
        return;
      }

      if (!isAuthenticated || !userId || !userExistsInDatabase || !isDatabaseInitialized) {
        logger.debug(
          `[RESUME-SYNC] triggerSyncSoon ignorado (${source}) - estado local incompleto ` +
          `(auth=${isAuthenticated}, userId=${Boolean(userId)}, existsInDb=${userExistsInDatabase}, dbReady=${isDatabaseInitialized})`
        );
        return;
      }

      if (!isNetworkConnected) {
        logger.debug(`[RESUME-SYNC] triggerSyncSoon ignorado (${source}) - rede offline no retorno`);
        return;
      }

      logger.info(`[RESUME-SYNC] Retorno detectado (${source}); antecipando sync periódico`);
      triggerSyncSoon(source, 1000);
    },
    [
      isAuthenticated,
      isDatabaseInitialized,
      isNetworkConnected,
      recoveryRuntimeSnapshot.status,
      userExistsInDatabase,
      userId,
    ]
  );

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const subscription = AppState.addEventListener('change', (nextAppState) => {
      const previousAppState = appStateRef.current;
      appStateRef.current = nextAppState;
      setIsRecoveryAppActive(nextAppState === 'active');

      if ((previousAppState === 'background' || previousAppState === 'inactive') && nextAppState === 'active') {
        logger.info(`[RESUME-SYNC] AppState retornou para active (${previousAppState} -> ${nextAppState})`);
        void runPendingCameraRecovery('app_state_active');
        checkPendingSupportRequests('foreground');
        checkAppUpdatePromptBestEffort(true);
        triggerResumeCatchUpSync('app-resume-mobile');
        return;
      }

      logger.debug(`[RESUME-SYNC] Mudança de AppState observada sem sync: ${previousAppState} -> ${nextAppState}`);
      if (nextAppState === 'background' || nextAppState === 'inactive') {
        flushBufferedLogEvents();
      }
    });

    return () => {
      subscription.remove();
    };
  }, [checkPendingSupportRequests, runPendingCameraRecovery, triggerResumeCatchUpSync]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    webWasHiddenRef.current = document.visibilityState !== 'visible';

    const handleVisibilityChange = () => {
      const visibilityState = document.visibilityState;
      const wasHidden = webWasHiddenRef.current;
      const isVisible = visibilityState === 'visible';

      webWasHiddenRef.current = !isVisible;
      setIsRecoveryAppActive(isVisible);

      if (wasHidden && isVisible) {
        logger.info('[RESUME-SYNC] Aba voltou a ficar visível');
        checkPendingSupportRequests('foreground_web');
        checkAppUpdatePromptBestEffort();
        triggerResumeCatchUpSync('app-resume-web');
        return;
      }

      logger.debug(`[RESUME-SYNC] visibilitychange observado sem sync: ${visibilityState} (wasHidden=${wasHidden})`);
      if (!isVisible) {
        flushBufferedLogEvents();
      }
    };

    const handlePageHide = () => {
      flushBufferedLogEvents();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [checkPendingSupportRequests, triggerResumeCatchUpSync]);

  // Efeito para verificar status da assinatura do usuário
  useEffect(() => {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      return;
    }

    const { enabled: recoveryRuntimeGateEnabled, mode: recoverySubscriptionLookup } =
      getRecoverySubscriptionLookupContext({
        canaryEnabled: IS_CHECKLIST_SYNC_RECOVERY_CANARY,
        platform: Platform.OS,
        snapshot: recoveryRuntimeSnapshot,
        userId,
      });

    if (recoverySubscriptionLookup === 'blocked') {
      return;
    }

    const clearIntervalIfNeeded = () => {
      if (subscriptionIntervalRef.current) {
        clearInterval(subscriptionIntervalRef.current);
        subscriptionIntervalRef.current = null;
      }
    };

    const isSubscriptionLookupCurrent = (empresaId: string): boolean => {
      const currentUserId = globalState.user.user.get()?.id;
      if (currentUserId !== userId) return false;
      if (!recoveryRuntimeGateEnabled) return true;
      const currentGate = recoveryRuntimeGate.getSnapshot();
      return currentGate.identity?.companyId === empresaId
        && decideRecoverySubscriptionLookup(currentGate, currentUserId)
          === recoverySubscriptionLookup;
    };

    const checkUserSubscriptionStatus = async (reason: string) => {
      if (!isAuthenticated || !userId || !userExistsInDatabase || !isDatabaseInitialized) {
        return;
      }

      if (isCheckingRef.current) {
        TabNavigatorLogger.Main.debug(`[ASSINATURA] Checagem ignorada (${reason}) pois uma verificação já está em andamento`);
        return;
      }

      try {
        isCheckingRef.current = true;
        setIsChecking(true);
        TabNavigatorLogger.Main.debug(`[ASSINATURA] Verificando status da assinatura do usuário (${reason})`);

        const localUser = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);

        TabNavigatorLogger.Main.debug(
          `Usuário local disponível: ${Boolean(localUser)}; empresa disponível: ${Boolean(localUser?.empresa_id)}`
        );

        if (localUser && localUser.empresa_id) {
          const empresaId = localUser.empresa_id;
          if (
            recoveryRuntimeGateEnabled
            && recoveryRuntimeSnapshot.identity?.companyId !== empresaId
          ) {
            return;
          }
          TabNavigatorLogger.Main.debug(`Empresa ID do usuário: ${empresaId}`);

          TabNavigatorLogger.Main.info(`[ASSINATURA] Iniciando verificação de assinatura para empresa: ${empresaId} (${reason})`);

          if (!isSubscriptionLookupCurrent(empresaId)) return;
          const subscriptionStatus = await checkSubscriptionStatus(empresaId, {
            allowRemote: recoverySubscriptionLookup === 'remote_allowed',
          });
          if (!isSubscriptionLookupCurrent(empresaId)) return;

          TabNavigatorLogger.Main.info(`[ASSINATURA] Status retornado: ${JSON.stringify(subscriptionStatus)}`);

          if (subscriptionStatus) {
            TabNavigatorLogger.Main.info(
              `[ASSINATURA] Atualizando estado global: ${subscriptionStatus.status}, Plano: ${subscriptionStatus.planType}`
            );

            userActions.setSubscriptionStatus(subscriptionStatus);
          }
        } else {
          TabNavigatorLogger.Main.warn('Usuário não possui empresa_id no banco local');
        }
      } catch (error) {
        TabNavigatorLogger.Main.error('Erro ao verificar status da assinatura:', error);
      } finally {
        TabNavigatorLogger.Main.info('[ASSINATURA] Finalizou checagem de assinatura');
        isCheckingRef.current = false;
        setIsChecking(false);
      }
    };

    clearIntervalIfNeeded();

    if (isAuthenticated && userId && userExistsInDatabase && isDatabaseInitialized) {
      const intervalMs = APP_CONSTANTS.TIMEOUTS?.SUBSCRIPTION_REVALIDATION_INTERVAL ?? 5 * 60 * 1000;

      checkUserSubscriptionStatus('initial');

      subscriptionIntervalRef.current = setInterval(() => {
        checkUserSubscriptionStatus('interval');
      }, intervalMs);
    }

    return () => {
      clearIntervalIfNeeded();
      isCheckingRef.current = false;
      setIsChecking(false);
    };
  }, [
    isAuthenticated,
    userId,
    userExistsInDatabase,
    isDatabaseInitialized,
    isNetworkConnected,
    recoveryRuntimeSnapshot.status,
    recoveryRuntimeSnapshot.blocked,
    recoveryRuntimeSnapshot.decision?.phase,
    recoveryRuntimeSnapshot.decision?.blocked,
  ]);

  useEffect(() => {
    const bootstrapExecution = ++bootstrapExecutionRef.current;
    let initialBootstrapRetryHandle: ReturnType<typeof setTimeout> | null = null;
    let initialBootstrapRetryAttempt = 0;
    const isCurrentUser = () => isBootstrapIdentityCurrent({
      execution: bootstrapExecution,
      activeExecution: bootstrapExecutionRef.current,
      expectedUserId: userId,
      currentUserId: globalState.user.user.get()?.id,
    });

    // Esta função é chamada quando o componente é montado e quando as dependências mudam
    const setupServices = async () => {
      let acquiredInitializationLock = false;
      const endPostUpdateBootstrap = beginPostUpdateBootstrap(userId);
      const resolveCurrentCompanyId = async (): Promise<string | null> => {
        if (!userId || !isCurrentUser()) return null;
        const profile = globalState.user.profile.get();
        if (profile?.id === userId && profile.empresa_id) return profile.empresa_id;
        const localUser = await databaseService.readItem<any>(
          APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
          userId
        );
        return isCurrentUser() ? localUser?.empresa_id || null : null;
      };
      const isCurrentScope = async (companyId: string) => {
        const currentCompanyId = await resolveCurrentCompanyId();
        return isBootstrapIdentityCurrent({
          execution: bootstrapExecution,
          activeExecution: bootstrapExecutionRef.current,
          expectedUserId: userId,
          currentUserId: globalState.user.user.get()?.id,
          expectedCompanyId: companyId,
          currentCompanyId,
        });
      };
      const markAppReady = async (message: string, companyId?: string) => {
        if (!isCurrentUser() || (companyId && !(await isCurrentScope(companyId)))) {
          logger.info(`${message} ignorada: bootstrap de identidade substituído`);
          return;
        }
        if (!globalState.app.isReady.get()) {
          logger.info(message);
          appActions.setReady(true);
        } else {
          logger.info(`${message} (estado já estava pronto)`);
        }
      };
      const scheduleInitialBootstrapRetry = (reason: string) => {
        if (!isCurrentUser() || globalState.app.isReady.get()) return;
        if (initialBootstrapRetryHandle) clearTimeout(initialBootstrapRetryHandle);
        const delayMs = Math.min(30000, 3000 * (2 ** initialBootstrapRetryAttempt));
        initialBootstrapRetryAttempt += 1;
        logger.warn(`${reason}. Nova tentativa do bootstrap em ${delayMs}ms`);
        initialBootstrapRetryHandle = setTimeout(() => {
          initialBootstrapRetryHandle = null;
          if (isCurrentUser() && !globalState.app.isReady.get()) {
            void setupServices();
          }
        }, delayMs);
      };

      try {
        //await debugPause('app app layout useEffect setupServices');
        logger.info('Iniciando setup dos serviços');
        // Verificar se já existe uma inicialização em andamento
        if (realtimeSetupInProgress.current || syncInProgress.current) {
          logger.info('Inicialização de serviços já em andamento, ignorando chamada duplicada');
          scheduleInitialBootstrapRetry('Inicialização ainda ocupada');
          return;
        }

        // Marcar início da inicialização
        realtimeSetupInProgress.current = true;
        acquiredInitializationLock = true;

        if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          logger.warn('[RECOVERY] Layout autenticado mantido inerte durante a quarentena');
          await markAppReady('Aplicacao pronta em modo de recuperacao');
          return;
        }

        // Inicializar o banco de dados primeiro, antes de verificar pré-requisitos
        if (!isDatabaseInitialized && userId) {
          try {
            logger.info(`Inicializando banco de dados para o usuário: ${userId}`);
            await databaseService.initialize(userId);
            logger.info('Banco de dados inicializado com sucesso');
            // Atualizar estado global para refletir que o banco está inicializado
            globalState.database.isInitialized.set(true);
          } catch (dbError) {
            logger.error('Erro ao inicializar banco de dados:', dbError);
          }
        }

        if (!isCurrentUser()) return;

        const currentProfile = globalState.user.profile.get();
        let syncCompanyId = currentProfile?.id === userId
          ? currentProfile.empresa_id || ''
          : '';
        if (!syncCompanyId && userId) {
          const localUser = await databaseService.readItem<any>(
            APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
            userId
          );
          syncCompanyId = localUser?.empresa_id || '';
        }

        if (!syncCompanyId || !(await isCurrentScope(syncCompanyId))) return;

        if (IS_CHECKLIST_SYNC_RECOVERY_CANARY && userId) {
          const gateSnapshot = await evaluateRecoveryRuntimeGate({ companyId: syncCompanyId, userId });
          if (!(await isCurrentScope(syncCompanyId))) return;
          if (gateSnapshot.blocked) {
            cleanupRealtimeSubscriptions();
            stopPeriodicSync();
            logger.warn(
              `[RECOVERY] Inicializacao de sync bloqueada antes do primeiro trafego: ${gateSnapshot.reason}`
            );
            await markAppReady('Aplicacao pronta para preservacao controlada', syncCompanyId);
            return;
          }
        }

        //logger.info('Verificando pré-requisitos para inicialização dos serviços de sincronização');

        // Verifica se todos os pré-requisitos estão atendidos
        const prerequisites = checkSyncPrerequisites();

        if (prerequisites.allMet) {
          logger.info('Pré-requisitos atendidos, inicializando serviços');

          // Inicializa o serviço de sincronização
          const syncInitialized = initializeSyncService();

          if (syncInitialized && userId) {
            const lastSyncTimestamp = getLastSyncTimestamp({ companyId: syncCompanyId, userId });
            const bootstrapScope = { companyId: syncCompanyId, userId };
            const shouldWaitForInitialSync =
              !lastSyncTimestamp || !isInitialBootstrapComplete(bootstrapScope);
            let realtimeInitialized = false;

            if (shouldWaitForInitialSync) {
              logger.info(
                'Bootstrap inicial ainda não confirmado. O sync será concluído antes de assinar o Realtime.'
              );
            } else {
              logger.info(`Inicializando realtime para o usuário: ${userId}`);
              realtimeInitialized = await setupRealtimeSubscriptions(userId, syncCompanyId);
              if (!(await isCurrentScope(syncCompanyId))) return;
              if (realtimeInitialized) {
                logger.info('Serviços de realtime inicializados com sucesso');
                logger.info(
                  `lastSyncTimestamp encontrado (${new Date(lastSyncTimestamp).toISOString()}). Aplicação será liberada antes do sync em background.`
                );
                await markAppReady('Aplicação pronta para uso com base local existente', syncCompanyId);
              } else {
                logger.error('Falha ao inicializar serviços de realtime');
                await markAppReady('Aplicação pronta para uso (modo degradado)', syncCompanyId);
              }
            }

            if (!(await isCurrentScope(syncCompanyId))) return;

            logger.info('Iniciando sincronização completa (push + pull)...');
            syncInProgress.current = true;
            const syncResult = shouldWaitForInitialSync
              ? await syncAll({
                  forceFullSync: true,
                  skipPush: true,
                  skipPendingMedia: true,
                  protectedBootstrapRepair: Boolean(lastSyncTimestamp),
                  tracePrefix: '[bootstrap-full]',
                })
              : await syncAll();

            if (bootstrapExecutionRef.current === bootstrapExecution) {
              syncInProgress.current = false;
            }
            if (!(await isCurrentScope(syncCompanyId))) return;

            if (syncResult.success) {
              logger.info('Sincronização concluída com sucesso:', syncResult.message);
            } else {
              logger.warn('Sincronização concluída com problemas:', syncResult.message);
            }
            reportClientInstallationPresenceBestEffort(syncResult.success ? 'success' : 'partial');
            checkAppUpdatePromptBestEffort();

            if (shouldWaitForInitialSync) {
              if (!syncResult.success) {
                logger.error('Bootstrap inicial mantido bloqueado: o full sync não foi concluído com sucesso');
                scheduleInitialBootstrapRetry('Full sync inicial incompleto');
                return;
              }

              logger.info(`Full sync inicial encerrado. Inicializando realtime para o usuário: ${userId}`);
              realtimeInitialized = await setupRealtimeSubscriptions(userId, syncCompanyId);
              if (!(await isCurrentScope(syncCompanyId))) return;

              if (!realtimeInitialized) {
                logger.error('Bootstrap inicial mantido bloqueado: falha ao assinar os canais realtime');
                scheduleInitialBootstrapRetry('Realtime inicial incompleto');
                return;
              }

              logger.info('Realtime assinado. Executando pull incremental de catch-up do bootstrap.');
              syncInProgress.current = true;
              const catchUpResult = await syncAll({
                skipPush: true,
                skipPendingMedia: true,
                tracePrefix: '[bootstrap-catchup]',
              });
              if (bootstrapExecutionRef.current === bootstrapExecution) {
                syncInProgress.current = false;
              }
              if (!(await isCurrentScope(syncCompanyId))) return;
              if (!catchUpResult.success) {
                logger.error('Bootstrap inicial mantido bloqueado: o catch-up incremental falhou');
                cleanupRealtimeSubscriptions();
                scheduleInitialBootstrapRetry('Catch-up inicial incompleto');
                return;
              }
              if (!(await isCurrentScope(syncCompanyId)) || isRecoveryRuntimeBlocked()) return;
              markInitialBootstrapComplete(bootstrapScope);
              // A base já está segura. Falhas no envio não devem prender a Home nem impedir a retomada.
              startPeriodicSync();
              await markAppReady('Aplicação pronta para uso; envios continuam em segundo plano', syncCompanyId);
              const hasPendingWork = await hasEligiblePendingSyncWork(syncCompanyId, userId);
              if (!(await isCurrentScope(syncCompanyId))) return;
              if (hasPendingWork) {
                logger.info('Bootstrap preparado; iniciando envio normal da fila elegível com a Home disponível.');
                syncInProgress.current = true;
                const pendingResult = await syncAll({ tracePrefix: '[bootstrap-push]' });
                if (bootstrapExecutionRef.current === bootstrapExecution) syncInProgress.current = false;
                if (!(await isCurrentScope(syncCompanyId))) return;
                logger[pendingResult.success ? 'info' : 'warn'](
                  pendingResult.success ? 'Fila pós-bootstrap confirmada:' : 'Fila pós-bootstrap ainda pendente:',
                  pendingResult.message
                );
                reportClientInstallationPresenceBestEffort(pendingResult.success ? 'success' : 'partial');
              } else {
                logger.info('Bootstrap preparado sem pendências elegíveis para envio.');
                reportClientInstallationPresenceBestEffort('success');
              }
              checkAppUpdatePromptBestEffort();
            }

            if (!(await isCurrentScope(syncCompanyId)) || isRecoveryRuntimeBlocked()) return;
            if (!shouldWaitForInitialSync) startPeriodicSync();
          } else {
            logger.warn('Falha ao inicializar serviço de sincronização');
            // Mesmo com falha na sincronização, consideramos a aplicação pronta (modo degradado)
            await markAppReady('Aplicação pronta para uso (modo degradado)', syncCompanyId);
          }
        } else {
          logger.warn('Pré-requisitos não atendidos para inicialização dos serviços');
          logger.info('Status dos pré-requisitos:', prerequisites);

          // Verificar se falha nos pré-requisitos é crítica ou não
          const isCriticalFailure = !prerequisites.userAuthenticated || !prerequisites.databaseInitialized;

          if (!isCriticalFailure) {
            // Se não for falha crítica, consideramos a aplicação pronta (modo degradado)
            await markAppReady('Aplicação pronta para uso (modo degradado - pré-requisitos parciais)', syncCompanyId);
          }
        }
      } catch (error) {
        logger.error('Erro ao inicializar serviços:', error);
        scheduleInitialBootstrapRetry('Erro transitório na inicialização dos serviços');
      } finally {
        // Marcar fim da inicialização, independente de sucesso ou erro
        if (acquiredInitializationLock && bootstrapExecutionRef.current === bootstrapExecution) {
          realtimeSetupInProgress.current = false;
          syncInProgress.current = false;
        }
        endPostUpdateBootstrap();
        if (isCurrentUser()) checkPendingSupportRequests('bootstrap_finished');
      }
    };

    // Chama a função de setup
    setupServices();

    // Função de limpeza quando o componente for desmontado
    return () => {
      if (initialBootstrapRetryHandle) clearTimeout(initialBootstrapRetryHandle);
      bootstrapExecutionRef.current += 1;
      realtimeSetupInProgress.current = false;
      syncInProgress.current = false;
      logger.info('Limpando serviços de realtime');
      cleanupRealtimeSubscriptions();
      stopPeriodicSync();

      // TODO: Implementar limpeza de outros recursos de sincronização quando necessário
      // cleanupSyncResources();
    };
  }, [
    isAuthenticated,
    userId,
    userExistsInDatabase,
    isDatabaseInitialized,
    recoveryReleaseEpoch,
  ]);

  // Efeito para monitorar mudanças de conectividade
  useEffect(() => {
    const reconnectExecution = ++reconnectExecutionRef.current;
    const bootstrapExecution = bootstrapExecutionRef.current;
    let reconnectCancelled = false;
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      return;
    }

    const reconnectAttempt = decideRecoveryReconnectAttempt({
      snapshot: recoveryRuntimeSnapshot,
      isOnline: isNetworkConnected,
      userId,
      attemptedIdentityKey: recoveryReconnectAttemptRef.current,
    });
    recoveryReconnectAttemptRef.current = reconnectAttempt.attemptedIdentityKey;

    if (IS_CHECKLIST_SYNC_RECOVERY_CANARY && isRecoveryRuntimeBlocked()) {
      cleanupRealtimeSubscriptions();
      stopPeriodicSync();
      if (reconnectAttempt.shouldEvaluate) {
        const identity = recoveryRuntimeSnapshot.identity!;
        void evaluateRecoveryRuntimeGate({
          companyId: identity.companyId,
          userId: identity.userId,
        });
      }
      return;
    }

    if (!isNetworkConnected) {
      logger.info('triggerSyncOnReconnectRede offline: desmontando subscriptions realtime');
      cleanupRealtimeSubscriptions();
      return; // não entra no bloco de reconexão
    }

    if (isNetworkConnected && userId && userExistsInDatabase) {
      logger.info('triggerSyncOnReconnect: Conexão de rede restabelecida, tentando reconectar serviços');

      // Verificar se já existe uma inicialização em andamento
      if (realtimeSetupInProgress.current) {
        logger.info('Inicialização de realtime já em andamento, ignorando reconexão');
        return;
      }
      if (syncInProgress.current) {
        logger.info('Sincronização já em andamento, ignorando reconexão');
        return;
      }

      const ownsReconnect = () => reconnectExecutionRef.current === reconnectExecution &&
        bootstrapExecutionRef.current === bootstrapExecution;
      const canReconnect = () => !reconnectCancelled && ownsReconnect() &&
        globalState.user.isAuthenticated.get() && globalState.database.isInitialized.get() &&
        canRunRecoveryReconnect({
        enabled: IS_CHECKLIST_SYNC_RECOVERY_CANARY,
        platform: Platform.OS,
        snapshot: recoveryRuntimeGate.getSnapshot(),
        expectedUserId: userId,
        expectedCompanyId: profileCompanyId,
        currentUserId: globalState.user.user.get()?.id,
        profileId: globalState.user.profile.get()?.id,
        profileCompanyId: globalState.user.profile.get()?.empresa_id,
        companyId: globalState.company.data.get()?.id,
        isOnline: globalState.network.isConnected.get(),
      });

      // Implementar item 4.7 - Reconexão e retry automático
      const triggerSyncOnReconnect = async () => {
        //await debugPause('app app layout useEffect triggerSyncOnReconnect');
        let schedulerResumed = false;
        try {
          if (!canReconnect() || !initializeSyncService()) return;
          // O Realtime já pode pedir snapshots durante o catch-up. O scheduler
          // precisa estar ativo para conservar essa continuação, inclusive no parcial.
          startPeriodicSync();
          schedulerResumed = true;
          // Marcar início da inicialização
          realtimeSetupInProgress.current = true;

          // Primeiro reconectar realtime
          logger.info('Tentando reconectar subscrições realtime');
          realtimeSetupInProgress.current = true;
          const realtimeReconnected = await setupRealtimeSubscriptions(userId, profileCompanyId!);

          if (!canReconnect()) return;
          realtimeSetupInProgress.current = false;
          if (realtimeReconnected) {
            logger.info('Subscrições realtime reconectadas com sucesso, verificando alterações pendentes...');

            // Verificar se há operações pendentes a enviar
            const pendingChanges = globalState.database.pendingChanges.get();

            // Iniciar sincronização independente de alterações pendentes
            logger.info(`${pendingChanges > 0 ? `Detectadas ${pendingChanges} alterações pendentes` : 'Sem alterações pendentes'}, iniciando sincronização automática...`);

            // Executar sincronização completa após reconexão
            syncInProgress.current = true;
            const syncResult = await syncAll();
            if (!canReconnect()) return;
            syncInProgress.current = false;

            if (syncResult.success) {
              logger.info('Sincronização após reconexão concluída com sucesso:', syncResult.message);
            } else {
              logger.warn('Sincronização após reconexão concluída com problemas:', syncResult.message);
            }
            reportClientInstallationPresenceBestEffort(syncResult.success ? 'success' : 'partial');
            checkAppUpdatePromptBestEffort();
          } else {
            logger.error('Falha ao reconectar subscrições realtime após reconexão');
          }
        } catch (error) {
          logger.error('Erro durante reconexão automática:', error);
        } finally {
          // Marcar fim da inicialização, independente de sucesso ou erro
          if (ownsReconnect()) {
            realtimeSetupInProgress.current = false;
            syncInProgress.current = false;
          }
          if (schedulerResumed && canReconnect()) triggerSyncSoon('reconnect-catchup', 2000);
        }
      };

      // Executar a reconexão com delay mínimo para evitar múltiplas tentativas simultâneas
      // Delay de 1000ms (1 segundo) é usado para:
      // 1. Dar tempo para a conexão se estabilizar completamente
      // 2. Evitar que múltiplas detecções de reconexão disparem várias sincronizações
      // 3. Reduzir carga no servidor em cenários de reconexão em massa
      recoveryReconnectTimerRef.current = setTimeout(() => {
        recoveryReconnectTimerRef.current = null;
        if (!canReconnect()) {
          logger.warn('[RECOVERY] Reconexao cancelada: gate ou identidade mudou durante o atraso');
          return;
        }
        void triggerSyncOnReconnect();
      }, 1000); // 1 segundo de delay
      return () => {
        reconnectCancelled = true;
        if (ownsReconnect()) {
          realtimeSetupInProgress.current = false;
          syncInProgress.current = false;
        }
        if (recoveryReconnectTimerRef.current) {
          clearTimeout(recoveryReconnectTimerRef.current);
          recoveryReconnectTimerRef.current = null;
        }
      };
    }
  }, [isNetworkConnected, recoveryRuntimeSnapshot.status, userId, profileCompanyId, userExistsInDatabase]);

  // Animação para ícone ativo
  const animateIcon = (index: number) => {
    setActiveTabIndex(index);
    Animated.sequence([
      Animated.timing(iconScale, {
        toValue: 1.2,
        duration: 100,
        useNativeDriver: true
      }),
      Animated.timing(iconScale, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true
      })
    ]).start();
  };

  // Configurar ícones para o navegador de abas
  const getTabBarIcon = (name: any) => {
    return ({ focused, color }: { focused: boolean; color: ColorValue }) => {
      let iconName = '';

      // Determinar qual ícone usar com base no nome da rota
      if (name === 'index') {
        iconName = focused ? 'home' : 'home-outline';
      } else if (name === 'admin') {
        iconName = focused ? 'view-dashboard' : 'view-dashboard-outline';
      } else if (name === 'painel-dev') {
        iconName = 'tools';
      } else if (name === 'settings') {
        iconName = focused ? 'cog' : 'cog-outline';
      }

      // Returna o componente de ícone com as propriedades corretas
      return (
        <Animated.View style={{ transform: [{ scale: focused ? iconScale : 1 }] }}>
          <MaterialCommunityIcons
            name={iconName as any}
            size={24}
            color={color}
            accessibilityLabel={
              name === 'index'
                ? 'Página inicial'
                : name === 'admin'
                  ? 'Painel'
                  : name === 'painel-dev'
                    ? 'Painel Dev'
                    : 'Configurações'
            }
          />
        </Animated.View>
      );
    };
  };

  // Personalizar as opções do tab navigator
  const tabScreenOptions = {
    tabBarActiveTintColor: theme.colors.primary.default,
    tabBarInactiveTintColor: theme.colors.text.secondary,
    tabBarLabelPosition: 'below-icon',
    tabBarStyle: {
      backgroundColor: theme.colors.background.primary,
      paddingBottom: Platform.OS === 'ios' ? 20 : androidBottomInset,
      height: Platform.OS === 'ios' ? 80 : 60 + androidBottomInset,
      paddingTop: 5,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      marginHorizontal: 0,
      ...styles.tabBar,
      ...(/^\/checklist\/[^/]+$/.test(pathname) ? { display: 'none' as const } : {}),
    },
    tabBarLabelStyle: {
      fontSize: 12,
      fontWeight: '500' as any,
      paddingBottom: Platform.OS === 'ios' ? 0 : 5
    },
    headerStyle: {
      backgroundColor: theme.colors.background.primary
    },
    headerTintColor: theme.colors.text.primary,
    headerTitleStyle: {
      fontWeight: theme.fontWeights.semibold as any
    },
    tabBarBackground: () => (
      <LinearGradient
        colors={[theme.colors.background.primary, theme.colors.background.tertiary]}
        style={{
          height: '100%',
          width: '100%',
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
        }}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
      />
    )
  };

  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    return <View style={styles.container} />;
  }

  if (IS_CHECKLIST_SYNC_RECOVERY_CANARY && shouldBlockRecoveryUi(recoveryRuntimeSnapshot)) {
    return (
      <RecoveryGateScreen
        gate={recoveryRuntimeSnapshot}
        runtime={appRecoverySnapshot}
        onConsent={() => void handleRecoveryConsent()}
        onResume={() => void handleRecoveryResume()}
        onVerify={() => void handleRecoveryVerify()}
      />
    );
  }

  // Layout responsivo que se adapta a diferentes plataformas
  if (isDesktopLayout) {
    // Layout para web com sidebar em orientação paisagem
    return (
      <View style={styles.webContainer}>
        <View style={styles.sidebar}>
          <LinearGradient
            colors={[theme.colors.background.primary, theme.colors.background.tertiary]}
            style={styles.sidebarGradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
          >
            <View style={styles.sidebarContent}>
              <NavLink
                href="/(app)"
                icon={<MaterialCommunityIcons name="home-outline" size={24} color={theme.colors.text.primary} />}
                label="Início"
                isActive={pathname === '/'}
              />
              {showAdminArea && (
                <NavLink
                  href="/(app)/admin"
                  icon={<MaterialCommunityIcons name="view-dashboard-outline" size={24} color={theme.colors.text.primary} />}
                  label="Painel"
                  isActive={pathname.startsWith('/(app)/admin')}
                />
              )}
              {isDeveloper && (
                <NavLink
                  href="/(app)/painel-dev"
                  icon={<MaterialCommunityIcons name="tools" size={24} color={theme.colors.text.primary} />}
                  label="Painel Dev"
                  isActive={pathname.startsWith('/(app)/painel-dev') || pathname.startsWith('/painel-dev')}
                />
              )}
              <NavLink
                href="/(app)/settings"
                icon={<MaterialCommunityIcons name="cog-outline" size={24} color={theme.colors.text.primary} />}
                label="Configurações"
                isActive={pathname === '/settings'}
              />
              <TouchableOpacity
                style={[styles.navLink, styles.whatsappNavLink]}
                onPress={handleOpenWhatsApp}
                accessibilityRole="button"
                accessibilityLabel="Ajuda no WhatsApp"
              >
                <View style={styles.navLinkIcon}>
                  <MaterialCommunityIcons name="whatsapp" size={24} color={theme.colors.util.success} />
                </View>
                <Text style={[styles.navLinkText, styles.navLinkWhatsAppText]}>
                  Ajuda
                </Text>
              </TouchableOpacity>
            </View>
          </LinearGradient>
        </View>
        <View style={styles.content}>
          <Slot />
        </View>
        <AppUpdatePromptModal prompt={appUpdatePrompt} />
      </View>
    );
  } else {
    // Layout para mobile ou web em orientação retrato com tabs
    return (
      <View style={styles.container}>
        <Tabs
          screenOptions={tabScreenOptions as any}
          initialRouteName="index"
          screenListeners={({ route, navigation }) => ({
            tabPress: (e) => {
              guardAppTabPress(e, route, navigation, appNavigationLeave);
              const target = e.target || '';
              const routeName = target.split('-')[0];
              if (routeName.includes('index')) {
                animateIcon(0);
              } else if (routeName.includes('admin')) {
                animateIcon(1);
              } else if (routeName.includes('painel-dev')) {
                animateIcon(2);
              } else if (routeName.includes('settings')) {
                animateIcon(3);
              } else if (routeName.includes('help')) {
                animateIcon(4);
              }
            }
          })}
        >
          <Tabs.Screen
            name="index"
            options={{
              title: "Início",
              tabBarIcon: getTabBarIcon('index'),
              headerTitle: "Início",
              headerShown: false
            }}
          />
          <Tabs.Screen
            name="admin"
            options={{
              title: "Painel",
              tabBarIcon: getTabBarIcon('admin'),
              headerShown: false,
              href: showAdminArea ? undefined : null,
            }}
          />
          <Tabs.Screen
            name="painel-dev/index"
            options={{
              title: "Dev",
              tabBarIcon: getTabBarIcon('painel-dev'),
              headerTitle: "Painel Dev",
              headerShown: false,
              href: isDeveloper ? undefined : null,
            }}
          />
          <Tabs.Screen
            name="settings"
            options={{
              title: "Config",
              tabBarIcon: getTabBarIcon('settings'),
              headerTitle: "Configurações",
              headerShown: false
            }}
          />
          <Tabs.Screen
            name="help"
            options={{
              title: "Ajuda",
              tabBarIcon: ({ focused }) => (
                <Animated.View style={{ transform: [{ scale: focused ? iconScale : 1 }] }}>
                  <MaterialCommunityIcons
                    name="whatsapp"
                    size={24}
                    color={focused ? theme.colors.primary.contrast : theme.colors.util.success}
                    accessibilityLabel="Ajuda"
                  />
                </Animated.View>
              ),
              headerShown: false,
            }}
            listeners={{
              tabPress: (e) => {
                e.preventDefault();
                handleOpenWhatsApp();
              },
            }}
          />
          {/* Rotas internas que NÃO devem aparecer como abas visíveis */}
          <Tabs.Screen name="debug" options={{ href: null }} />
          <Tabs.Screen name="checklists" options={{ href: null }} />
          <Tabs.Screen name="field-ordering" options={{ href: null }} />
          <Tabs.Screen name="painel-dev/explorar-checklists" options={{ href: null }} />
          <Tabs.Screen name="painel-dev/empresa/[id]" options={{ href: null }} />
          <Tabs.Screen
            name="painel-dev/checklist/[id]"
            options={{
              href: null,
              headerShown: false,
            }}
          />
          {/* Rota dinâmica de checklist */}
          <Tabs.Screen
            name="checklist/[id]"
            options={{
              href: null,
              headerShown: false,
            }}
          />
        </Tabs>
        <AppUpdatePromptModal prompt={appUpdatePrompt} />
      </View>
    );
  }
});

const AppLayout = () => <AppNavigationLeaveProvider><AppLayoutContent /></AppNavigationLeaveProvider>;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
    paddingBottom: 0,
  },
  tabBar: {
    borderTopWidth: 0,
    borderTopColor: 'transparent',
    shadowColor: theme.colors.text.primary,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
  // Estilos para a versão web
  webContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.background.secondary,
  },
  sidebar: {
    width: 220,
    backgroundColor: theme.colors.background.primary,
    borderRightWidth: 1,
    borderRightColor: theme.colors.border.light,
    shadowColor: theme.colors.text.primary,
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 5,
  },
  sidebarGradient: {
    flex: 1,
  },
  sidebarContent: {
    paddingTop: 20,
    flex: 1,
  },
  navLink: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginHorizontal: 8,
    marginVertical: 4,
    borderRadius: 8,
  },
  navLinkActive: {
    backgroundColor: `${theme.colors.primary.default}20`,
  },
  whatsappNavLink: {
    marginTop: 8,
  },
  navLinkIcon: {
    marginRight: 12,
  },
  navLinkText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    fontWeight: '500' as any,
  },
  navLinkTextActive: {
    color: theme.colors.text.primary,
    fontWeight: '600' as any,
  },
  navLinkWhatsAppText: {
    color: theme.colors.util.success,
    fontWeight: '600' as any,
  },
  content: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary
  },
});

export default AppLayout;
